import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchApi } from '../../lib/api';
import { Button, Card, Input, Textarea } from '../../components/ui';

interface Room {
  id: string;
  name: string;
  building: string;
  capacity: number;
  equipment?: string[] | null;
  floor?: string | null;
  currentOccupancy?: number;
  isAtCapacity?: boolean;
}

interface RoomSubmitData {
  name: string;
  building: string;
  capacity: number;
  equipment?: string[];
  floor?: string;
}

interface RoomFormData {
  name: string;
  building: string;
  capacity: string;
  equipment: string;
  floor: string;
}

export default function RoomManager() {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState<Room | null>(null);
  const [formData, setFormData] = useState<RoomFormData>({
    name: '',
    building: '',
    capacity: '',
    equipment: '',
    floor: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof RoomFormData, string>>>({});
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Fetch rooms with occupancy data
  const { data: rooms, isLoading: roomsLoading } = useQuery<Room[]>({
    queryKey: ['admin', 'rooms'],
    queryFn: () => fetchApi<Room[]>('/admin/rooms'),
  });

  // Create room mutation
  const createMutation = useMutation({
    mutationFn: (data: RoomSubmitData) =>
      fetchApi('/admin/rooms', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'rooms'] });
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      showNotification('success', 'Room created successfully');
      resetForm();
    },
    onError: (error: Error) => {
      showNotification('error', `Failed to create room: ${error.message}`);
    },
  });

  // Update room mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: RoomSubmitData }) =>
      fetchApi(`/admin/rooms/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'rooms'] });
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      showNotification('success', 'Room updated successfully');
      resetForm();
    },
    onError: (error: Error) => {
      showNotification('error', `Failed to update room: ${error.message}`);
    },
  });

  // Delete room mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      fetchApi(`/admin/rooms/${id}`, {
        method: 'DELETE',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'rooms'] });
      queryClient.invalidateQueries({ queryKey: ['rooms'] });
      showNotification('success', 'Room deleted successfully');
    },
    onError: (error: Error) => {
      showNotification('error', `Failed to delete room: ${error.message}`);
    },
  });

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof RoomFormData, string>> = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.building.trim()) {
      errors.building = 'Building is required';
    }
    if (!formData.capacity.trim()) {
      errors.capacity = 'Capacity is required';
    } else {
      const capacityNum = parseInt(formData.capacity, 10);
      if (isNaN(capacityNum) || capacityNum <= 0) {
        errors.capacity = 'Capacity must be a positive number';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Parse equipment from comma-separated string to array
    const equipment = formData.equipment
      .split(',')
      .map((e) => e.trim())
      .filter((e) => e.length > 0);

    const submitData: RoomSubmitData = {
      name: formData.name,
      building: formData.building,
      capacity: parseInt(formData.capacity, 10),
      equipment: equipment.length > 0 ? equipment : undefined,
      floor: formData.floor.trim() || undefined,
    };

    if (editingRoom) {
      updateMutation.mutate({ id: editingRoom.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (room: Room) => {
    setEditingRoom(room);
    setFormData({
      name: room.name,
      building: room.building,
      capacity: room.capacity.toString(),
      equipment: room.equipment?.join(', ') || '',
      floor: room.floor || '',
    });
    setFormErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = (id: string, name: string) => {
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      deleteMutation.mutate(id);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      building: '',
      capacity: '',
      equipment: '',
      floor: '',
    });
    setFormErrors({});
    setEditingRoom(null);
    setIsFormOpen(false);
  };

  if (roomsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-usc-garnet mx-auto"></div>
          <p className="mt-4 text-usc-gray-600">Loading rooms...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-usc-garnet">Room Management</h2>
          <p className="text-usc-gray-600 mt-2">Create and manage conference rooms</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          + Create Room
        </Button>
      </div>

      {/* Notification Toast */}
      {notification && (
        <div
          className={`mb-4 p-4 rounded-lg ${
            notification.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {notification.message}
        </div>
      )}

      {/* Room Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-2xl font-bold text-usc-garnet mb-6">
                {editingRoom ? 'Edit Room' : 'Create New Room'}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Room Name *"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  error={formErrors.name}
                  placeholder="e.g., Conference Room A"
                />

                <Input
                  label="Building *"
                  value={formData.building}
                  onChange={(e) => setFormData({ ...formData, building: e.target.value })}
                  error={formErrors.building}
                  placeholder="e.g., Swearingen Engineering Center"
                />

                <Input
                  label="Capacity *"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  error={formErrors.capacity}
                  placeholder="e.g., 50"
                  min="1"
                />

                <Input
                  label="Floor"
                  value={formData.floor}
                  onChange={(e) => setFormData({ ...formData, floor: e.target.value })}
                  placeholder="e.g., 2nd Floor"
                />

                <Textarea
                  label="Equipment"
                  value={formData.equipment}
                  onChange={(e) => setFormData({ ...formData, equipment: e.target.value })}
                  placeholder="Comma-separated list (e.g., Projector, Whiteboard, Microphone)"
                  rows={3}
                  helperText="Enter equipment items separated by commas"
                />

                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    {createMutation.isPending || updateMutation.isPending
                      ? 'Saving...'
                      : editingRoom
                      ? 'Update Room'
                      : 'Create Room'}
                  </Button>
                </div>
              </form>
            </div>
          </Card>
        </div>
      )}

      {/* Rooms List */}
      <div className="space-y-4">
        {rooms && rooms.length > 0 ? (
          rooms.map((room) => (
            <Card key={room.id} className="p-6">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-bold text-usc-garnet">{room.name}</h3>
                    {room.isAtCapacity && (
                      <span className="px-3 py-1 bg-red-100 text-red-800 text-sm font-medium rounded-full">
                        At Capacity
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="font-medium text-usc-gray-600">Building:</span>{' '}
                      <span className="text-usc-gray-900">{room.building}</span>
                    </div>
                    {room.floor && (
                      <div>
                        <span className="font-medium text-usc-gray-600">Floor:</span>{' '}
                        <span className="text-usc-gray-900">{room.floor}</span>
                      </div>
                    )}
                    <div>
                      <span className="font-medium text-usc-gray-600">Capacity:</span>{' '}
                      <span className="text-usc-gray-900">
                        {room.currentOccupancy !== undefined
                          ? `${room.currentOccupancy} / ${room.capacity}`
                          : room.capacity}
                      </span>
                    </div>
                    {room.equipment && room.equipment.length > 0 && (
                      <div className="md:col-span-2">
                        <span className="font-medium text-usc-gray-600">Equipment:</span>{' '}
                        <span className="text-usc-gray-900">{room.equipment.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex space-x-2 ml-4">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleEdit(room)}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => handleDelete(room.id, room.name)}
                    disabled={deleteMutation.isPending}
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </Card>
          ))
        ) : (
          <Card className="p-12 text-center">
            <p className="text-usc-gray-600 text-lg">No rooms found</p>
            <p className="text-usc-gray-500 mt-2">Create your first room to get started</p>
          </Card>
        )}
      </div>
    </div>
  );
}
